import type {
  PaginatedResponseDto,
  PayoutOptionsResponseDto,
  WithdrawalRecordDto,
  WithdrawalRequestDto,
} from '../../domain/contracts/api.contracts';
import { apiClient } from './apiClient';
import {
  withdrawalFromWire,
  withdrawalPageFromWire,
  withdrawalRequestToWire,
  withdrawalStatusToWire,
} from './wireAdapters';

/** E19 query params (orchestrator §6). */
export interface WithdrawalHistoryParams {
  page?: number;
  limit?: number;
  status?: 'PENDING_VERIFICATION' | 'PENDING_ADMIN' | 'AUTHORIZED' | 'DISBURSED' | 'REJECTED' | 'EXPIRED';
}

/** Optional `Idempotency-Key` header: a replay of the same key returns the stored result with no second debit. */
const idempotencyHeaders = (key?: string) => (key ? { headers: { 'Idempotency-Key': key } } : undefined);

/** E14 — POST /withdrawals/request { amount, method }; 422 PAYOUT_DETAILS_MISSING when the rail has no saved details. */
export async function requestWithdrawal(
  body: WithdrawalRequestDto,
  idempotencyKey?: string,
): Promise<WithdrawalRecordDto> {
  const { data } = await apiClient.post<Record<string, unknown>>(
    '/withdrawals/request',
    withdrawalRequestToWire(body),
    idempotencyHeaders(idempotencyKey),
  );
  return withdrawalFromWire(data);
}

/** GET /withdrawals/payout-options — the rails the member has saved payout details for (accounts come masked). */
export async function getPayoutOptions(): Promise<PayoutOptionsResponseDto> {
  const { data } = await apiClient.get<Partial<PayoutOptionsResponseDto>>('/withdrawals/payout-options');
  return { options: data.options ?? [] };
}

/** E19 — GET /withdrawals/history (paged) */
export async function getWithdrawalHistory(
  params: WithdrawalHistoryParams,
): Promise<PaginatedResponseDto<WithdrawalRecordDto>> {
  const wire = params.status ? { ...params, status: withdrawalStatusToWire(params.status) } : params;
  const { data } = await apiClient.get<PaginatedResponseDto<Record<string, unknown>>>(
    '/withdrawals/history',
    { params: wire },
  );
  return withdrawalPageFromWire(data);
}
